import { Component } from "@theme/component";

/**
 * @typedef {{
 *   chips?: HTMLButtonElement[],
 *   items?: HTMLElement[],
 *   grid?: HTMLElement,
 *   matchStatus?: HTMLElement,
 * }} FaqCategoriesRefs
 */

/**
 * @extends {Component<FaqCategoriesRefs>}
 */
class FaqCategories extends Component {
  /** @type {string} */
  activeCategory = "";

  connectedCallback() {
    super.connectedCallback();
    this.activeCategory = this.dataset.activeCategory ?? "";
  }

  /** @param {Event} e */
  handleChipClick(e) {
    const button = /** @type {HTMLButtonElement} */ (e.target);
    const category = button.dataset.category;

    if (!category || category === this.activeCategory) return;

    this.filterByCategory(category);
  }

  /**
   * @param {HTMLElement} element
   * @returns {Promise<void>}
   */
  #waitForTransition(element) {
    return new Promise((resolve) => {
      let settled = false;

      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        element.removeEventListener("transitionend", done);
        resolve();
      };

      element.addEventListener("transitionend", done);
      const timeout = setTimeout(done, 300);
    });
  }

  /** @param {string} category */
  async filterByCategory(category) {
    const items = this.refs.items ?? [];
    const chips = this.refs.chips ?? [];

    const visibleItems = items.filter(
      (item) => !item.classList.contains("is-hidden"),
    );

    for (const item of visibleItems) {
      item.classList.add("is-fading");
    }

    const firstVisible = visibleItems[0];
    if (firstVisible) {
      await this.#waitForTransition(firstVisible);
    }

    let matchCount = 0;

    for (const item of items) {
      const categoriesAttr = item.dataset.categories ?? "";
      const isMatch = categoriesAttr.split(",").includes(category);
      const shouldShow = categoriesAttr === "" || isMatch;

      if (isMatch) matchCount++;
      item.classList.toggle("is-hidden", !shouldShow);
      item.classList.remove("is-fading");
    }

    for (const chip of chips) {
      const isActive = chip.dataset.category === category;
      chip.setAttribute("aria-pressed", isActive ? "true" : "false");

      const chipBadge = chip.querySelector(".chip-badge");
      if (chipBadge) {
        chipBadge.classList.toggle("as-black-chip", isActive);
        chipBadge.classList.toggle("as-filled", isActive);
      }
    }

    this.activeCategory = category;
    this.dataset.activeCategory = category;

    const newVisibleItems = items.filter(
      (item) => !item.classList.contains("is-hidden"),
    );

    for (const item of newVisibleItems) {
      item.classList.add("is-fading");
    }

    if (this.refs.grid) this.refs.grid.offsetHeight;

    for (const item of newVisibleItems) {
      item.classList.remove("is-fading");
    }

    this.#announceMatchCount(category, matchCount);
  }

  /**
   * @param {string} category
   * @param {number} count
   */
  #announceMatchCount(category, count) {
    const status = this.refs.matchStatus;
    if (!(status instanceof HTMLElement)) return;

    const chip = (this.refs.chips ?? []).find(
      (item) => item.dataset.category === category,
    );
    const categoryName = (chip?.textContent ?? "").replace(/\s+/g, " ").trim();
    const template =
      count === 1
        ? (status.dataset.matchOne ?? "")
        : (status.dataset.matchOther ?? "");

    status.textContent = template
      .replace(/__COUNT__/g, () => String(count))
      .replace(/__CATEGORY__/g, () => categoryName);
  }
}

if (!customElements.get("faq-categories")) {
  customElements.define("faq-categories", FaqCategories);
}
